/**
 * A small, self-contained Markdown dialect used to persist SlateEditor's
 * formatting (bold/italic/underline/code/quote/lists, paragraph indents,
 * tracked insertions/deletions and figure placeholders) as plain text.
 *
 * This is NOT a general-purpose Markdown/CommonMark implementation — it only
 * needs to round-trip exactly what `slateNodesToMarkdown` produces. Plain,
 * unformatted text serializes to the exact same one-line-per-paragraph shape
 * the editor used to store before rich formatting existed, so pre-existing
 * plain-text content keeps working unchanged.
 */
import React from 'react';
import { Tooltip } from '@mui/material';
import { Text } from 'slate';
import { Paragraph, TextRun, ImageRun, HeadingLevel, Table, TableRow, TableCell, WidthType } from 'docx';

// ---- inline marks ----------------------------------------------------

// Escape characters that would otherwise be mistaken for our own inline
// delimiters (backslash escape, like Markdown itself).
function escapeInlineText(text) {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/`/g, '\\`')
    .replace(/\*/g, '\\*')
    .replace(/</g, '\\<');
}

function serializeLeaf(leaf) {
  if (leaf.code) {
    // Code spans are literal — no nested-mark or escaping concerns inside.
    return '`' + String(leaf.text).replace(/`/g, '\\`') + '`';
  }
  let text = escapeInlineText(leaf.text || '');
  if (leaf.italic) text = `*${text}*`;
  if (leaf.bold) text = `**${text}**`;
  if (leaf.underline) text = `<u>${text}</u>`;
  // Comment markers are added after escaping so the < chars in the marker are literal
  if (leaf.commentId) text = `<!--cmt:${leaf.commentId}-->${text}<!--/cmt-->`;
  // Tracked changes wrap everything else; the payload (author, time) is URI-encoded JSON
  if (leaf.ins) text = `<!--ins:${encodeChange(leaf.ins)}-->${text}<!--/ins-->`;
  if (leaf.del) text = `<!--del:${encodeChange(leaf.del)}-->${text}<!--/del-->`;
  return text;
}

// ---- tracked changes -----------------------------------------------------
// A change payload is { a: author id, n: author name, t: ISO time }.

function encodeChange(change) {
  return encodeURIComponent(JSON.stringify(change));
}

function decodeChange(raw) {
  try { return JSON.parse(decodeURIComponent(raw)); } catch { return { a: '', n: 'Unknown', t: '' }; }
}

const CHANGE_MARKERS = [
  { open: '<!--ins:', close: '<!--/ins-->', mark: 'ins' },
  { open: '<!--del:', close: '<!--/del-->', mark: 'del' },
];

function leavesToMarkdown(children) {
  return (children || []).map(serializeLeaf).join('');
}

// Single-pass recursive-descent inline parser — the inverse of
// serializeLeaf/escapeInlineText. Handles `\` escapes and nested marks
// (e.g. "**_bold italic_**") without needing a generic tokenizer.
function parseInlineFrom(text, start, marks, stopDelim) {
  const leaves = [];
  let buf = '';
  let i = start;
  const flush = () => {
    if (buf !== '') {
      leaves.push({ text: buf, ...marks });
      buf = '';
    }
  };

  while (i < text.length) {
    if (stopDelim && text.startsWith(stopDelim, i)) {
      flush();
      return { leaves, next: i + stopDelim.length };
    }
    const ch = text[i];
    if (ch === '\\' && i + 1 < text.length) {
      buf += text[i + 1];
      i += 2;
      continue;
    }
    let change = null;
    for (const marker of CHANGE_MARKERS) {
      if (text.startsWith(marker.open, i)) { change = marker; break; }
    }
    if (change) {
      flush();
      const markerEnd = text.indexOf('-->', i + change.open.length);
      if (markerEnd !== -1) {
        const payload = decodeChange(text.slice(i + change.open.length, markerEnd));
        const res = parseInlineFrom(text, markerEnd + 3, { ...marks, [change.mark]: payload }, change.close);
        leaves.push(...res.leaves);
        i = res.next;
        continue;
      }
    }
    if (text.startsWith('<!--cmt:', i)) {
      flush();
      const markerEnd = text.indexOf('-->', i + 8);
      if (markerEnd !== -1) {
        const uuid = text.slice(i + 8, markerEnd);
        const res = parseInlineFrom(text, markerEnd + 3, { ...marks, commentId: uuid }, '<!--/cmt-->');
        leaves.push(...res.leaves);
        i = res.next;
        continue;
      }
    }
    if (text.startsWith('<u>', i)) {
      flush();
      const res = parseInlineFrom(text, i + 3, { ...marks, underline: true }, '</u>');
      leaves.push(...res.leaves);
      i = res.next;
      continue;
    }
    if (ch === '`') {
      flush();
      let j = i + 1;
      let code = '';
      while (j < text.length && text[j] !== '`') {
        if (text[j] === '\\' && j + 1 < text.length) { code += text[j + 1]; j += 2; }
        else { code += text[j]; j += 1; }
      }
      leaves.push({ text: code, ...marks, code: true });
      i = j + 1;
      continue;
    }
    if (text.startsWith('**', i)) {
      flush();
      const res = parseInlineFrom(text, i + 2, { ...marks, bold: true }, '**');
      leaves.push(...res.leaves);
      i = res.next;
      continue;
    }
    if (ch === '*') {
      flush();
      const res = parseInlineFrom(text, i + 1, { ...marks, italic: true }, '*');
      leaves.push(...res.leaves);
      i = res.next;
      continue;
    }
    buf += ch;
    i += 1;
  }
  flush();
  return { leaves, next: i };
}

function parseInline(text) {
  const { leaves } = parseInlineFrom(text || '', 0, {}, null);
  return leaves.length ? leaves : [{ text: '' }];
}

// ---- block level -------------------------------------------------------

function slateNodeToMarkdown(node) {
  switch (node.type) {
    case 'heading-one':
      return `# ${leavesToMarkdown(node.children)}`;
    case 'heading-two':
      return `## ${leavesToMarkdown(node.children)}`;
    case 'block-quote':
      return leavesToMarkdown(node.children)
        .split('\n')
        .map(line => `> ${line}`)
        .join('\n');
    case 'bulleted-list':
      return (node.children || []).map(li => `- ${leavesToMarkdown(li.children)}`).join('\n');
    case 'numbered-list':
      return (node.children || []).map((li, idx) => `${idx + 1}. ${leavesToMarkdown(li.children)}`).join('\n');
    case 'table': {
      const rows = node.children || [];
      if (rows.length === 0) return '';
      const colCount = Math.max(...rows.map(r => (r.children || []).length));
      const mdRows = rows.map(row => {
        const cells = (row.children || []).map(cell =>
          leavesToMarkdown(cell.children).replace(/\|/g, '\\|').replace(/\n/g, ' ')
        );
        while (cells.length < colCount) cells.push('');
        return `| ${cells.join(' | ')} |`;
      });
      // GFM: separator row after the first (header) row
      const sep = `|${Array(colCount).fill(' --- |').join('')}`;
      mdRows.splice(1, 0, sep);
      return mdRows.join('\n');
    }
    case 'figure':
      // Figure placeholder: caption and note to authors, filled in locally
      return `<!--figure:${encodeURIComponent(JSON.stringify({
        caption: node.caption || '', note: node.note || '', ...(node.figureId ? { figure_id: node.figureId } : {}),
      }))}-->`;
    default:
      // Indented paragraphs carry a line-prefix marker
      return (node.indent > 0 ? `<!--indent:${node.indent}-->` : '') + leavesToMarkdown(node.children);
  }
}

/** Serialize a Slate document (array of top-level nodes) to our Markdown dialect. */
export function slateNodesToMarkdown(nodes) {
  return (nodes || []).map(slateNodeToMarkdown).join('\n');
}

// A separator row contains only pipes, dashes, colons, and spaces (GFM alignment syntax)
const _isTableSep = line => /^\|[-|\s:]+\|$/.test(line.trim());
// A table row starts and ends with a pipe
const _isTableRow = line => { const t = line.trim(); return t.startsWith('|') && t.endsWith('|'); };

function _parseTableRow(line) {
  const trimmed = line.trim();
  // Remove leading and trailing |
  const inner = trimmed.slice(1, -1);
  // split on unescaped pipes only: a cell may contain "\|" (written by slateNodeToMarkdown)
  const cells = inner.split(/(?<!\\)\|/).map(c => c.trim());
  return {
    type: 'table-row',
    children: cells.map(c => ({
      type: 'table-cell',
      // parseInline handles \| → | via its backslash-escape logic
      children: parseInline(c),
    })),
  };
}

/** Parse our Markdown dialect back into a Slate document. */
export function markdownToSlateNodes(markdown) {
  if (markdown == null || markdown === '') {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
  const lines = String(markdown).split('\n');
  const nodes = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (_isTableRow(line)) {
      // Consume all table rows (including separator row) as a block
      const tableLines = [];
      while (i < lines.length && (_isTableRow(lines[i]) || _isTableSep(lines[i]))) {
        tableLines.push(lines[i]);
        i += 1;
      }
      const dataRows = tableLines.filter(l => !_isTableSep(l)).map(_parseTableRow);
      if (dataRows.length > 0) {
        nodes.push({ type: 'table', children: dataRows });
      }
    } else if (/^> ?/.test(line)) {
      const group = [];
      while (i < lines.length && /^> ?/.test(lines[i])) {
        group.push(lines[i].replace(/^> ?/, ''));
        i += 1;
      }
      nodes.push({ type: 'block-quote', children: parseInline(group.join('\n')) });
    } else if (/^- /.test(line)) {
      const items = [];
      while (i < lines.length && /^- /.test(lines[i])) {
        items.push({ type: 'list-item', children: parseInline(lines[i].slice(2)) });
        i += 1;
      }
      nodes.push({ type: 'bulleted-list', children: items });
    } else if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push({ type: 'list-item', children: parseInline(lines[i].replace(/^\d+\. /, '')) });
        i += 1;
      }
      nodes.push({ type: 'numbered-list', children: items });
    } else if (/^## /.test(line)) {
      nodes.push({ type: 'heading-two', children: parseInline(line.slice(3)) });
      i += 1;
    } else if (/^# /.test(line)) {
      nodes.push({ type: 'heading-one', children: parseInline(line.slice(2)) });
      i += 1;
    } else if (/^<!--figure:.*-->$/.test(line)) {
      let data = {};
      try { data = JSON.parse(decodeURIComponent(line.slice(11, -3))); } catch { /* keep an empty placeholder */ }
      nodes.push({
        type: 'figure', caption: data.caption || '', note: data.note || '',
        ...(data.figure_id ? { figureId: data.figure_id } : {}),
        children: [{ text: '' }],
      });
      i += 1;
    } else {
      const indent = line.match(/^<!--indent:(\d+)-->/);
      if (indent) {
        nodes.push({ type: 'paragraph', indent: Number(indent[1]), children: parseInline(line.slice(indent[0].length)) });
      } else {
        nodes.push({ type: 'paragraph', children: parseInline(line) });
      }
      i += 1;
    }
  }
  return nodes.length ? nodes : [{ type: 'paragraph', children: [{ text: '' }] }];
}

/** True if every top-level Slate node has no content (used for "is this empty?" checks. */
export function isEmptySlateDocument(nodes) {
  return (nodes || []).every(n => (n.children || []).every(c => Text.isText(c) && !c.text));
}

// ---- React rendering (read-only previews) ------------------------------

const CITATION_RE = /([ \t]*)(\[\d+(?:\s*[,–-]\s*\d+)*\])/g;
const SUPERSCRIPT_START = /^[⁰¹²³⁴⁵⁶⁷⁸⁹]/;

// In-text citations in read-only previews: shown in the publication's style, the full references on hover.
// citations = { display: Map(marker -> rendered text), title: marker -> [reference strings] }
function renderCitations(text, citations, key) {
  const parts = [];
  let last = 0;
  text.replace(CITATION_RE, (whole, space, marker, offset) => {
    const lines = citations.title?.(marker) || [];
    if (!lines.length) return whole;                      // not a known citation: leave the text alone
    const rendered = citations.display?.get(marker) ?? marker;
    parts.push(text.slice(last, offset));
    if (!SUPERSCRIPT_START.test(rendered)) parts.push(space);
    parts.push(
      <Tooltip key={`${key}-c${offset}`} arrow placement="top"
        title={<span style={{ display: 'block', maxWidth: 420, fontSize: 13, lineHeight: 1.45 }}>{lines.map((l) => <span key={l} style={{ display: 'block', margin: '3px 0' }}>{l}</span>)}</span>}>
        <span style={{ color: '#1565c0', cursor: 'help', borderBottom: '1px dotted rgba(21,101,192,0.5)' }}>{rendered}</span>
      </Tooltip>,
    );
    last = offset + whole.length;
    return whole;
  });
  if (!parts.length) return text;
  parts.push(text.slice(last));
  return parts;
}

function renderLeafReact(leaf, key, citations) {
  const text = citations && !leaf.code ? renderCitations(leaf.text, citations, key) : leaf.text;
  let node = leaf.code
    ? <code key={key} style={{ backgroundColor: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: 3 }}>{leaf.text}</code>
    : <React.Fragment key={key}>{text}</React.Fragment>;
  if (leaf.bold) node = <strong key={key}>{node}</strong>;
  if (leaf.italic) node = <em key={key}>{node}</em>;
  if (leaf.underline) node = <u key={key}>{node}</u>;
  return node;
}

// Read-only views show the text as if all tracked changes were accepted
function renderLeavesReact(children, citations) {
  return (children || []).filter(leaf => !leaf.del).map((leaf, idx) => renderLeafReact(leaf, idx, citations));
}

/**
 * Render our Markdown dialect as React elements for read-only display
 * (Preview tab, tooltips, etc.) — a lightweight alternative to re-mounting a
 * full SlateEditor just to show formatted text.
 */
export function renderMarkdownToReact(markdown, { figureCounter = { n: 0 }, figures = {}, citations = null } = {}) {
  const nodes = markdownToSlateNodes(markdown);
  return nodes.map((node, idx) => {
    switch (node.type) {
      case 'figure': {
        // Numbered across the document when callers share one figureCounter
        figureCounter.n += 1;
        const image = node.figureId && figures[node.figureId];
        if (image) {
          return (
            <figure key={idx} style={{ margin: '16px 0', textAlign: 'center' }}>
              <img src={image.url} alt={node.caption || `Figure ${figureCounter.n}`}
                   style={{ maxWidth: '100%', maxHeight: 520, objectFit: 'contain' }} />
              <figcaption style={{ marginTop: 6, textAlign: 'left' }}>
                <strong>Figure {figureCounter.n}.</strong> {node.caption}
              </figcaption>
            </figure>
          );
        }
        return (
          <div key={idx} style={{ border: '2px dashed #9fa8da', borderRadius: 6, padding: '16px 20px', margin: '12px 0', textAlign: 'center', background: 'rgba(63,81,181,0.04)' }}>
            <div style={{ fontWeight: 600, color: '#3949ab' }}>Insert Figure {figureCounter.n} here</div>
            <div style={{ marginTop: 6 }}><strong>Figure {figureCounter.n}.</strong> {node.caption}</div>
            {node.note && <div style={{ marginTop: 4, fontSize: '0.85em', opacity: 0.7 }}>{node.note}</div>}
          </div>
        );
      }
      case 'heading-one':
        return <h3 key={idx}>{renderLeavesReact(node.children, citations)}</h3>;
      case 'heading-two':
        return <h4 key={idx}>{renderLeavesReact(node.children, citations)}</h4>;
      case 'block-quote':
        return (
          <blockquote key={idx} style={{ borderLeft: '3px solid #ddd', paddingLeft: 16, margin: '8px 0', color: 'inherit', opacity: 0.85 }}>
            {renderLeavesReact(node.children, citations)}
          </blockquote>
        );
      case 'bulleted-list':
        return <ul key={idx} style={{ margin: '4px 0', paddingLeft: 24 }}>{(node.children || []).map((li, i) => <li key={i}>{renderLeavesReact(li.children, citations)}</li>)}</ul>;
      case 'numbered-list':
        return <ol key={idx} style={{ margin: '4px 0', paddingLeft: 24 }}>{(node.children || []).map((li, i) => <li key={i}>{renderLeavesReact(li.children, citations)}</li>)}</ol>;
      case 'table':
        return (
          <div key={idx} style={{ overflowX: 'auto', margin: '6px 0' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'inherit' }}>
              <tbody>
                {(node.children || []).map((row, ri) => (
                  <tr key={ri}>
                    {(row.children || []).map((cell, ci) => {
                      const isHeader = ri === 0;
                      const Tag = isHeader ? 'th' : 'td';
                      return (
                        <Tag
                          key={ci}
                          style={{
                            border: '1px solid rgba(0,0,0,0.18)',
                            padding: '4px 8px',
                            textAlign: 'left',
                            backgroundColor: isHeader ? 'rgba(0,0,0,0.04)' : 'transparent',
                            fontWeight: isHeader ? 600 : 'inherit',
                          }}
                        >
                          {renderLeavesReact(cell.children, citations)}
                        </Tag>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return (
          <p key={idx} style={{ margin: '0 0 8px 0', paddingLeft: node.indent ? `${node.indent * 2}em` : undefined }}>
            {renderLeavesReact(node.children, citations)}
          </p>
        );
    }
  });
}

// ---- Word (.docx) export ------------------------------------------------

function leavesToDocxRuns(children) {
  const leaves = (children || []).filter(leaf => !leaf.del); // accepted view

  if (leaves.length === 0) return [new TextRun('')];
  return leaves.map(leaf => new TextRun({
    text: leaf.text || '',
    bold: !!leaf.bold,
    italics: !!leaf.italic,
    underline: leaf.underline ? {} : undefined,
    font: leaf.code ? 'Courier New' : undefined,
  }));
}

/**
 * Convert our Markdown dialect into an array of docx `Paragraph` objects,
 * preserving bold/italic/underline/code/quote/lists — used by the proposal's
 * Word export so it matches the formatting shown in the Preview tab.
 */
export function markdownToDocxParagraphs(markdown, { figureCounter = { n: 0 }, figureImages = {} } = {}) {
  const nodes = markdownToSlateNodes(markdown);
  const paragraphs = [];
  nodes.forEach(node => {
    switch (node.type) {
      case 'figure': {
        figureCounter.n += 1;
        // Uploaded image (PNG/JPEG from the server; Word cannot hold WebP), at most 6 in wide
        const image = node.figureId && figureImages[node.figureId];
        if (image) {
          const scale = Math.min(1, 576 / image.width, 720 / image.height);
          paragraphs.push(new Paragraph({
            alignment: 'center',
            spacing: { before: 240, after: 120 },
            children: [new ImageRun({
              type: image.type, data: image.data,
              transformation: { width: Math.round(image.width * scale), height: Math.round(image.height * scale) },
            })],
          }));
          paragraphs.push(new Paragraph({
            children: [new TextRun({ text: `Figure ${figureCounter.n}. `, bold: true }), new TextRun(node.caption || '')],
            spacing: { after: 240 },
          }));
          break;
        }
        const border = { color: '9FA8DA', space: 8, style: 'dashed', size: 12 };
        paragraphs.push(new Paragraph({
          alignment: 'center',
          border: { top: border, bottom: border, left: border, right: border },
          spacing: { before: 240, after: 120 },
          children: [new TextRun({ text: `[Insert Figure ${figureCounter.n} here]`, bold: true, color: '3949AB' })],
        }));
        paragraphs.push(new Paragraph({
          children: [new TextRun({ text: `Figure ${figureCounter.n}. `, bold: true }), new TextRun(node.caption || '')],
          spacing: { after: node.note ? 60 : 240 },
        }));
        if (node.note) {
          paragraphs.push(new Paragraph({ children: [new TextRun({ text: node.note, italics: true, size: 18 })], spacing: { after: 240 } }));
        }
        break;
      }
      case 'heading-one':
        paragraphs.push(new Paragraph({ heading: HeadingLevel.HEADING_1, children: leavesToDocxRuns(node.children) }));
        break;
      case 'heading-two':
        paragraphs.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: leavesToDocxRuns(node.children) }));
        break;
      case 'block-quote':
        paragraphs.push(new Paragraph({
          children: leavesToDocxRuns(node.children),
          indent: { left: 480 },
          border: { left: { color: 'CCCCCC', space: 8, style: 'single', size: 12 } },
        }));
        break;
      case 'bulleted-list':
        (node.children || []).forEach(li => {
          paragraphs.push(new Paragraph({
            children: [new TextRun('• '), ...leavesToDocxRuns(li.children)],
            indent: { left: 360 },
          }));
        });
        break;
      case 'numbered-list':
        (node.children || []).forEach((li, idx) => {
          paragraphs.push(new Paragraph({
            children: [new TextRun(`${idx + 1}. `), ...leavesToDocxRuns(li.children)],
            indent: { left: 360 },
          }));
        });
        break;
      case 'table': {
        const tableRows = (node.children || []).map((row, ri) =>
          new TableRow({
            children: (row.children || []).map(cell =>
              new TableCell({
                children: [new Paragraph({ children: leavesToDocxRuns(cell.children) })],
                shading: ri === 0 ? { fill: 'E8E8E8' } : undefined,
              })
            ),
          })
        );
        paragraphs.push(new Table({
          rows: tableRows,
          width: { size: 100, type: WidthType.PERCENTAGE },
        }));
        break;
      }
      default:
        paragraphs.push(new Paragraph({
          children: leavesToDocxRuns(node.children),
          indent: node.indent ? { left: 720 * node.indent } : undefined,
        }));
    }
  });
  return paragraphs;
}

/** Plain text of the accepted version (tracked deletions dropped, markers removed). */
export function markdownToPlainText(markdown) {
  return markdownToSlateNodes(markdown)
    .map(node => {
      const leaves = node.children?.[0]?.children ? node.children.flatMap(c => c.children || []) : node.children;
      return (leaves || []).filter(l => !l.del).map(l => l.text || '').join('');
    })
    .join('\n');
}

